import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner } from 'react-bootstrap'
import { stockItemCategoryAPI } from '../api/stockItemCategoryAPI'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'

function StockItemCategoriesListScreen() {
    const { hasPermission } = usePermissions()
    const canModify = hasPermission('stock.categories.modify')
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [draggedIndex, setDraggedIndex] = useState(null)
    const [dragOverIndex, setDragOverIndex] = useState(null)
    const [saving, setSaving] = useState(false)
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()

    const fetchCategories = useCallback(async () => {
        try {
            setLoading(true)
            const data = await stockItemCategoryAPI.getAll()
            setCategories(data)
        } catch (error) {
            console.error('Error fetching categories:', error)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchCategories()
    }, [fetchCategories])

    const filteredCategories = searchTerm
        ? categories.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
        : categories

    const isDragEnabled = canModify && !searchTerm && !saving

    const handleClearSearch = () => {
        setSearchInputValue('')
        setSearchTerm('')
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current)
        }
    }

    const handleSearchChange = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            () => {},
            debounceTimeoutRef
        )
    }

    const handleDragStart = (e, index) => {
        setDraggedIndex(index)
        e.dataTransfer.effectAllowed = 'move'
        e.currentTarget.style.opacity = '0.4'
    }

    const handleDragEnd = (e) => {
        e.currentTarget.style.opacity = '1'
        setDraggedIndex(null)
        setDragOverIndex(null)
    }

    const handleDragOver = (e, index) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (index !== draggedIndex) {
            setDragOverIndex(index)
        }
    }

    const handleDragLeave = () => {
        setDragOverIndex(null)
    }

    const handleDrop = async (e, targetIndex) => {
        e.preventDefault()
        if (draggedIndex === null || draggedIndex === targetIndex) {
            setDraggedIndex(null)
            setDragOverIndex(null)
            return
        }

        const newCategories = [...categories]
        const [movedItem] = newCategories.splice(draggedIndex, 1)
        newCategories.splice(targetIndex, 0, movedItem)

        const updatedCategories = newCategories.map((cat, i) => ({
            ...cat,
            display_order: i
        }))

        setCategories(updatedCategories)
        setDraggedIndex(null)
        setDragOverIndex(null)

        setSaving(true)
        try {
            const order = updatedCategories.map((cat, i) => ({
                _id: cat._id,
                display_order: i
            }))
            await stockItemCategoryAPI.reorder(order)
        } catch (error) {
            console.error('Error saving category order:', error)
            alert('Failed to save category order')
            fetchCategories()
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this category?')) {
            try {
                await stockItemCategoryAPI.delete(id)
                setCategories(categories.filter(category => category._id !== id))
            } catch (error) {
                console.error('Error deleting category:', error)
                alert('Failed to delete category')
            }
        }
    }

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Stock Item Categories</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search categories..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                {canModify && (
                    <Col xs="auto">
                        <Button
                            onClick={() => navigate('/stock-item-category/create')}
                            variant="success"
                        >
                            Create Stock Item Category
                        </Button>
                    </Col>
                )}
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <div style={{ minHeight: '350px' }}>
                    <table className="table table-striped table-hover" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                {isDragEnabled && (
                                    <th style={{ width: '50px', padding: '0.5rem', border: '1px solid var(--bs-table-border-color)' }}></th>
                                )}
                                <th style={{ padding: '0.5rem', border: '1px solid var(--bs-table-border-color)' }}>Name</th>
                                <th style={{ width: '100px', padding: '0.5rem', border: '1px solid var(--bs-table-border-color)' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCategories.map((category, index) => {
                                const actualIndex = categories.indexOf(category)
                                return (
                                    <tr
                                        key={category._id}
                                        draggable={isDragEnabled}
                                        onDragStart={(e) => handleDragStart(e, actualIndex)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={(e) => handleDragOver(e, actualIndex)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={(e) => handleDrop(e, actualIndex)}
                                        onClick={() => navigate(`/stock-item-category/${category._id}`)}
                                        style={{
                                            cursor: 'pointer',
                                            borderTop: dragOverIndex === actualIndex && draggedIndex !== null && draggedIndex > actualIndex
                                                ? '3px solid var(--bs-primary)'
                                                : undefined,
                                            borderBottom: dragOverIndex === actualIndex && draggedIndex !== null && draggedIndex < actualIndex
                                                ? '3px solid var(--bs-primary)'
                                                : undefined
                                        }}
                                    >
                                        {isDragEnabled && (
                                            <td style={{
                                                width: '50px',
                                                padding: '0.5rem',
                                                border: '1px solid var(--bs-table-border-color)',
                                                textAlign: 'center',
                                                cursor: 'grab',
                                                userSelect: 'none',
                                                fontSize: '16px',
                                                color: 'var(--bs-secondary)'
                                            }}>
                                                ⠿
                                            </td>
                                        )}
                                        <td style={{ padding: '0.5rem', border: '1px solid var(--bs-table-border-color)' }}>
                                            {highlightText(category.name, searchTerm)}
                                        </td>
                                        <td style={{ width: '100px', padding: '0.5rem', border: '1px solid var(--bs-table-border-color)' }}>
                                            {canModify && (
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleDelete(category._id)
                                                    }}
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </Container>
    )
}

export default StockItemCategoriesListScreen
